import type { Roll, RollGroup, RollSection, Scan, DownloadFormat, DownloadRequest } from '../types/roll';

/**
 * Validates a roll name input.
 * Returns null if valid (non-whitespace, trimmed length ≤ 100),
 * or an error string describing the issue.
 */
export function validateRollName(input: string): string | null {
  const trimmed = input.trim();
  if (trimmed.length === 0) {
    return 'Roll name cannot be empty';
  }
  if (trimmed.length > ROLL_NAME_MAX_LENGTH) {
    return `Roll name must be ${ROLL_NAME_MAX_LENGTH} characters or fewer`;
  }
  return null;
}

/** Maximum length of a roll name, mirroring the backend cap. */
export const ROLL_NAME_MAX_LENGTH = 100;

/** Maximum length of a roll's lab notes, mirroring the backend cap. */
export const LAB_NOTES_MAX_LENGTH = 2000;

/** What an admin roll card is doing, and what the admin can do about it. */
export type RollProcessingState =
  /** No confirmed run: the roll is waiting for uploads, or for the admin to confirm them. */
  | 'awaiting'
  /** A confirmed run is outstanding — some scans have no thumbnail yet. */
  | 'processing'
  /** Every scan on a confirmed roll has its thumbnail. */
  | 'ready'
  /** A confirmed run left scans whose masters could not be read; they need re-uploading. */
  | 'unreadable';

export interface RollScanState {
  thumbnail_ready: boolean;
  /** Set when the uploaded master could not be decoded; such a scan never becomes ready. */
  master_error?: string;
}

/**
 * Derives what stage a roll is at from its confirmation flag and its scans.
 *
 * `confirmed` on its own is not enough to claim a run is in flight. A roll can
 * carry a stale confirmation — the run it described covered scans that have
 * since been reset away, or the admin uploaded a fresh batch afterwards — and
 * reading the flag literally is what used to strand a roll at "generating
 * thumbnails" with nothing generating: no scans to finish, and no way to
 * confirm again because the roll already counted as confirmed.
 *
 * So a confirmed roll with no scans is `awaiting`, the same as one that was
 * never confirmed. The flag only describes a live run while there are scans it
 * could apply to.
 */
export function deriveRollProcessingState(
  confirmed: boolean,
  scans: RollScanState[]
): RollProcessingState {
  if (!confirmed || scans.length === 0) return 'awaiting';
  if (scans.some((s) => s.master_error)) return 'unreadable';
  return scans.every((s) => s.thumbnail_ready) ? 'ready' : 'processing';
}

/**
 * Whether the admin should be offered a restart of a roll's thumbnail run.
 *
 * Restart is the escape hatch for a confirmed roll whose thumbnails never
 * arrived: a workflow that failed part-way, or scans uploaded after the roll
 * was confirmed, which no run has ever covered. Both look identical from here —
 * confirmed, with work outstanding — and both are fixed by starting a fresh
 * run over whatever the roll currently holds.
 *
 * A roll that is genuinely still mid-run also qualifies; the cost of restarting
 * one of those is a duplicate execution writing the same derivatives, so the
 * button asks for confirmation rather than being hidden behind a timer we would
 * have to guess at.
 */
export function canRestartRollProcessing(
  confirmed: boolean,
  scans: RollScanState[]
): boolean {
  const state = deriveRollProcessingState(confirmed, scans);
  return state === 'processing' || state === 'unreadable';
}

/**
 * Sorts rolls by created_at in ascending order (oldest first).
 * Returns a new array — does not mutate the input.
 */
export function sortRollsByCreatedAt(rolls: Roll[]): Roll[] {
  return [...rolls].sort(
    (a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
  );
}

/**
 * Converts an array of RollGroup objects into RollSection objects,
 * preserving the count and order from the API response.
 */
export function buildRollSections(rolls: RollGroup[]): RollSection[] {
  return rolls.map((roll) => ({
    roll_id: roll.roll_id,
    roll_name: roll.roll_name,
    roll_type: roll.roll_type,
    lab_notes: roll.lab_notes,
    is_empty: roll.is_empty,
    scans: roll.scans,
    tracking: roll.tracking,
  }));
}

/**
 * Flattens all scans across all rolls into a single array,
 * preserving roll order and intra-roll scan order.
 * Used for lightbox navigation across roll boundaries.
 */
export function flattenScansForLightbox(rolls: RollGroup[]): Scan[] {
  return rolls.flatMap((roll) => roll.scans);
}

/**
 * Builds the download request body with proper deduplication:
 * - roll_ids: rolls where ALL scans are selected (via roll checkbox)
 * - scan_ids: individually selected scans NOT already covered by a selected roll
 */
export function buildDownloadRequest(
  selectedRolls: Set<string>,
  selectedScans: Set<string>,
  rolls: RollGroup[],
  format: DownloadFormat
): DownloadRequest {
  const rollIds = Array.from(selectedRolls);

  // Collect scan_ids covered by selected rolls
  const coveredScanIds = new Set<string>();
  for (const roll of rolls) {
    if (selectedRolls.has(roll.roll_id)) {
      for (const scan of roll.scans) {
        coveredScanIds.add(scan.scan_id);
      }
    }
  }

  // scan_ids are individually selected scans NOT covered by a selected roll
  const scanIds = Array.from(selectedScans).filter((id) => !coveredScanIds.has(id));

  const request: DownloadRequest = { format };
  if (rollIds.length > 0) {
    request.roll_ids = rollIds;
  }
  if (scanIds.length > 0) {
    request.scan_ids = scanIds;
  }
  return request;
}

/**
 * The download request for a set of scans. A roll whose every scan is in the
 * set goes by its roll id, which keeps the request small; the rest go by scan.
 */
export function downloadRequestFor(
  scanIds: Set<string>,
  rolls: RollGroup[],
  format: DownloadFormat
): DownloadRequest {
  const wholeRolls = new Set(
    rolls
      .filter((roll) => roll.scans.length > 0 && roll.scans.every((s) => scanIds.has(s.scan_id)))
      .map((roll) => roll.roll_id)
  );
  return buildDownloadRequest(wholeRolls, scanIds, rolls, format);
}
